/**
 * WishlistForm Component
 * Form for creating/editing wishlists
 */

import { useState } from 'react'
import { WishlistFormData } from '@/types'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Textarea } from '@/components/common/Textarea'

interface WishlistFormProps {
  initialData?: Partial<WishlistFormData>
  onSubmit: (data: WishlistFormData) => Promise<void>
  onCancel: () => void
  submitLabel?: string
}

export const WishlistForm = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Create Wishlist',
}: WishlistFormProps) => {
  const [formData, setFormData] = useState<WishlistFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    emoji: initialData?.emoji || '',
    occasion: initialData?.occasion || 'general',
    targetDate: initialData?.targetDate || '',
    visibility: initialData?.visibility || 'private',
    allowReservations: initialData?.allowReservations ?? true,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <Input
        id="title"
        label="Title"
        type="text"
        required
        maxLength={100}
        value={formData.title}
        onChange={e => setFormData({ ...formData, title: e.target.value })}
        placeholder="My Birthday Wishlist"
      />

      {/* Emoji */}
      <Input
        id="emoji"
        label="Emoji (optional)"
        type="text"
        maxLength={2}
        value={formData.emoji}
        onChange={e => setFormData({ ...formData, emoji: e.target.value })}
        placeholder="🎂"
      />

      {/* Description */}
      <Textarea
        id="description"
        label="Description"
        rows={3}
        maxLength={500}
        value={formData.description}
        onChange={e => setFormData({ ...formData, description: e.target.value })}
        placeholder="Describe your wishlist..."
      />

      {/* Occasion */}
      <div>
        <label htmlFor="occasion" className="block text-sm font-medium text-gray-700 mb-1">
          Occasion
        </label>
        <select
          id="occasion"
          value={formData.occasion}
          onChange={e => setFormData({ ...formData, occasion: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="general">General</option>
          <option value="birthday">Birthday</option>
          <option value="christmas">Christmas</option>
          <option value="wedding">Wedding</option>
          <option value="anniversary">Anniversary</option>
          <option value="baby-shower">Baby Shower</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Target Date */}
      <Input
        id="targetDate"
        label="Target Date (optional)"
        type="date"
        value={formData.targetDate}
        onChange={e => setFormData({ ...formData, targetDate: e.target.value })}
      />

      {/* Visibility */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="visibility"
              value="private"
              checked={formData.visibility === 'private'}
              onChange={e => setFormData({ ...formData, visibility: e.target.value as 'private' | 'shared' })}
              className="text-blue-600"
            />
            <div>
              <div className="font-medium">Private</div>
              <div className="text-sm text-gray-500">Only you can see this wishlist</div>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="visibility"
              value="shared"
              checked={formData.visibility === 'shared'}
              onChange={e => setFormData({ ...formData, visibility: e.target.value as 'private' | 'shared' })}
              className="text-blue-600"
            />
            <div>
              <div className="font-medium">Shared</div>
              <div className="text-sm text-gray-500">Anyone with the link can view and reserve items</div>
            </div>
          </label>
        </div>
      </div>

      {/* Allow Reservations */}
      <div>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={formData.allowReservations}
            onChange={e => setFormData({ ...formData, allowReservations: e.target.checked })}
            className="w-5 h-5 text-blue-600 rounded"
          />
          <span className="text-sm font-medium text-gray-700">
            Allow others to reserve items on this wishlist
          </span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
